import React, { createContext, forwardRef, useCallback, useContext, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Transforms } from 'slate';
import { ReactEditor, useSlateStatic } from 'slate-react';
import {
  deleteColumn,
  deleteRow,
  deleteTable,
  getColumnWidths,
  insertColumnLeft,
  insertColumnRight,
  insertRowAbove,
  insertRowBelow,
  setColumnWidths,
} from './table.utils';
import './Table.scss';

// ============================================================================
// Table Drag Context - manages row drag state at table level
// ============================================================================

const TableDragContext = createContext(null);

// ============================================================================
// Row Context - shares row state with cells
// ============================================================================

const RowContext = createContext(null);

// ============================================================================
// Table Element Component (with column resizing and row drag-drop)
// ============================================================================

export const TableElement = forwardRef(({ attributes, children, element }, ref) => {
  const editor = useSlateStatic();
  const tableRef = useRef(null);
  const [resizing, setResizing] = useState(null);
  const [resizeLine, setResizeLine] = useState(null);

  // Row drag-and-drop state
  const [dragState, setDragState] = useState({
    isDragging: false,
    dragRowIndex: null,
    dropTargetIndex: null,
    dropPosition: null, // 'above' or 'below'
  });

  // Use a ref to track current drag state to avoid stale closures
  const dragStateRef = useRef(dragState);
  dragStateRef.current = dragState;

  // Get the path to this table element
  const getTablePath = useCallback(() => {
    try {
      return ReactEditor.findPath(editor, element);
    } catch {
      return null;
    }
  }, [editor, element]);

  // Handle row drag start
  const handleRowDragStart = useCallback((rowIndex) => {
    setDragState({
      isDragging: true,
      dragRowIndex: rowIndex,
      dropTargetIndex: null,
      dropPosition: null,
    });
  }, []);

  // Handle row drag over
  const handleRowDragOver = useCallback((rowIndex, position) => {
    setDragState((prev) => ({
      ...prev,
      dropTargetIndex: rowIndex,
      dropPosition: position,
    }));
  }, []);

  // Handle row drag end / drop
  const handleRowDrop = useCallback(() => {
    // Use ref to get current drag state (avoids stale closure)
    const { dragRowIndex, dropTargetIndex, dropPosition } = dragStateRef.current;

    if (dragRowIndex === null || dropTargetIndex === null || dropPosition === null) {
      setDragState({
        isDragging: false,
        dragRowIndex: null,
        dropTargetIndex: null,
        dropPosition: null,
      });
      return;
    }

    // Calculate the actual target index
    let targetIndex = dropPosition === 'below' ? dropTargetIndex + 1 : dropTargetIndex;

    // Don't move if dropping in the same position
    if (dragRowIndex === targetIndex || dragRowIndex + 1 === targetIndex) {
      setDragState({
        isDragging: false,
        dragRowIndex: null,
        dropTargetIndex: null,
        dropPosition: null,
      });
      return;
    }

    const tablePath = getTablePath();

    if (!tablePath) {
      setDragState({
        isDragging: false,
        dragRowIndex: null,
        dropTargetIndex: null,
        dropPosition: null,
      });
      return;
    }

    // Adjust target if moving down (since removing the source shifts indices)
    if (dragRowIndex < targetIndex) {
      targetIndex -= 1;
    }

    const fromPath = [...tablePath, dragRowIndex];
    const toPath = [...tablePath, targetIndex];

    // Clear selection before moving to avoid path errors
    Transforms.deselect(editor);

    // Move the row
    Transforms.moveNodes(editor, {
      at: fromPath,
      to: toPath,
    });

    setDragState({
      isDragging: false,
      dragRowIndex: null,
      dropTargetIndex: null,
      dropPosition: null,
    });
  }, [editor, getTablePath]);

  // Handle drag cancel
  const handleRowDragCancel = useCallback(() => {
    setDragState({
      isDragging: false,
      dragRowIndex: null,
      dropTargetIndex: null,
      dropPosition: null,
    });
  }, []);

  // Handle column resize start
  const handleResizeStart = useCallback(
    (e, columnIndex) => {
      e.preventDefault();
      e.stopPropagation();

      const tableRect = tableRef.current?.getBoundingClientRect();
      if (!tableRect) {
        return;
      }

      const startX = e.clientX;
      const columnWidths = getColumnWidths(element, tableRect.width);
      const startWidth = columnWidths[columnIndex] || 100;

      setResizing({ columnIndex, startX, startWidth, columnWidths });
      setResizeLine({ x: e.clientX, tableTop: tableRect.top, tableHeight: tableRect.height });

      const handleMouseMove = (moveEvent) => {
        const deltaX = moveEvent.clientX - startX;
        const newWidth = Math.max(50, startWidth + deltaX);

        setResizeLine({
          x: moveEvent.clientX,
          tableTop: tableRect.top,
          tableHeight: tableRect.height,
        });

        const newColumnWidths = [...columnWidths];
        newColumnWidths[columnIndex] = newWidth;

        if (columnIndex < newColumnWidths.length - 1) {
          const nextWidth = columnWidths[columnIndex + 1] - deltaX;
          newColumnWidths[columnIndex + 1] = Math.max(50, nextWidth);
        }

        setResizing((prev) => ({ ...prev, currentWidths: newColumnWidths }));
      };

      const handleMouseUp = (upEvent) => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);

        const deltaX = upEvent.clientX - startX;
        const newWidth = Math.max(50, startWidth + deltaX);

        const newColumnWidths = [...columnWidths];
        newColumnWidths[columnIndex] = newWidth;

        if (columnIndex < newColumnWidths.length - 1) {
          const nextWidth = columnWidths[columnIndex + 1] - deltaX;
          newColumnWidths[columnIndex + 1] = Math.max(50, nextWidth);
        }

        const tablePath = getTablePath();
        if (tablePath) {
          setColumnWidths(editor, tablePath, newColumnWidths);
        }

        setResizing(null);
        setResizeLine(null);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [editor, element, getTablePath]
  );

  const currentWidths =
    resizing?.currentWidths || (element.columnWidths?.length > 0 ? element.columnWidths : null);

  const dragContextValue = {
    dragState,
    onDragStart: handleRowDragStart,
    onDragOver: handleRowDragOver,
    onDrop: handleRowDrop,
    onDragCancel: handleRowDragCancel,
    onResizeStart: handleResizeStart,
  };

  return (
    <TableDragContext.Provider value={dragContextValue}>
      <div className='table-wrapper'>
        <table
          className='styled-table'
          ref={(node) => {
            tableRef.current = node;
            if (typeof ref === 'function') {
              ref(node);
            } else if (ref) {
              ref.current = node;
            }
          }}
          {...attributes}
        >
          {currentWidths && (
            <colgroup contentEditable={false}>
              {currentWidths.map((width, i) => (
                <col
                  key={`col-${i}`}
                  style={{ width: `${width}px` }}
                />
              ))}
            </colgroup>
          )}
          <tbody>{children}</tbody>
        </table>
        {resizeLine &&
          ReactDOM.createPortal(
            <div
              className='resize-line'
              style={{
                left: `${resizeLine.x}px`,
                top: `${resizeLine.tableTop}px`,
                height: `${resizeLine.tableHeight}px`,
              }}
            />,
            document.body
          )}
      </div>
    </TableDragContext.Provider>
  );
});

TableElement.displayName = 'TableElement';

// ============================================================================
// Table Row Element Component (with drag handle)
// ============================================================================

export const TableRowElement = forwardRef(({ attributes, children, element }, ref) => {
  const editor = useSlateStatic();
  const dragContext = useContext(TableDragContext);
  const rowRef = useRef(null);
  const [isHovering, setIsHovering] = useState(false);

  // Get row index from Slate path
  let rowIndex = 0;
  const rowPath = ReactEditor.findPath(editor, element);
  rowIndex = rowPath[rowPath.length - 1];

  const isDragging =
    dragContext?.dragState?.isDragging && dragContext?.dragState?.dragRowIndex === rowIndex;
  const showDropIndicator =
    dragContext?.dragState?.isDragging &&
    dragContext?.dragState?.dropTargetIndex === rowIndex &&
    dragContext?.dragState?.dragRowIndex !== rowIndex;

  // Handle drag handle mouse down
  const handleDragMouseDown = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();

      if (!dragContext) {
        return;
      }
      dragContext.onDragStart(rowIndex);

      const handleMouseMove = (moveEvent) => {
        const tbody = rowRef.current?.closest('tbody');
        const rowElements = tbody?.querySelectorAll(':scope > tr');

        if (!rowElements || rowElements.length === 0) {
          return;
        }

        for (let i = 0; i < rowElements.length; i++) {
          const rect = rowElements[i].getBoundingClientRect();
          if (moveEvent.clientY >= rect.top && moveEvent.clientY <= rect.bottom) {
            const midpoint = rect.top + rect.height / 2;
            const position = moveEvent.clientY < midpoint ? 'above' : 'below';
            dragContext.onDragOver(i, position);
            break;
          }
        }
      };

      const handleMouseUp = () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        dragContext.onDrop();
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [dragContext, rowIndex]
  );

  // Row context value for cells to consume
  const rowContextValue = {
    onResizeStart: dragContext?.onResizeStart,
    totalColumns: element.children?.length || 1,
    handleDragMouseDown,
    isDragging,
    isHovering,
    showDropIndicator,
    dropPosition: dragContext?.dragState?.dropPosition,
  };

  return (
    <RowContext.Provider value={rowContextValue}>
      <tr
        className='styled-table-row'
        ref={(node) => {
          rowRef.current = node;
          if (typeof ref === 'function') {
            ref(node);
          } else if (ref) {
            ref.current = node;
          }
        }}
        {...attributes}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        style={{ opacity: isDragging ? 0.5 : 1 }}
      >
        {children}
      </tr>
    </RowContext.Provider>
  );
});

TableRowElement.displayName = 'TableRowElement';

// ============================================================================
// Table Cell Element Component (with context menu, resize handle, and drag handle)
// ============================================================================

export const TableCellElement = forwardRef(({ attributes, children, element }, ref) => {
  const editor = useSlateStatic();
  const rowContext = useContext(RowContext);
  const [contextMenu, setContextMenu] = useState(null);
  const [isHoveringResize, setIsHoveringResize] = useState(false);

  const cellPath = ReactEditor.findPath(editor, element);
  const cellIndex = cellPath[cellPath.length - 1];

  const totalColumns = rowContext?.totalColumns || 1;
  const onResizeStart = rowContext?.onResizeStart;

  const isFirstCell = cellIndex === 0;
  const handleDragMouseDown = rowContext?.handleDragMouseDown;
  const isDragging = rowContext?.isDragging;
  const isHovering = rowContext?.isHovering;
  const showDropIndicator = rowContext?.showDropIndicator;
  const dropPosition = rowContext?.dropPosition;

  const showResizeHandle = cellIndex < totalColumns - 1;

  const handleResizeMouseDown = useCallback(
    (e) => {
      if (onResizeStart) {
        onResizeStart(e, cellIndex);
      }
    },
    [onResizeStart, cellIndex]
  );

  const handleContextMenu = useCallback((e) => {
    e.preventDefault();

    const menuHeight = 240;
    const menuWidth = 200;
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    let x = e.clientX;
    let y = e.clientY;

    if (y + menuHeight > viewportHeight) {
      y = Math.max(10, viewportHeight - menuHeight - 10);
    }

    if (x + menuWidth > viewportWidth) {
      x = Math.max(10, viewportWidth - menuWidth - 10);
    }

    setContextMenu({ x, y });
  }, []);

  const closeContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  const handleAction = useCallback(
    (action) => {
      closeContextMenu();
      action(editor);
    },
    [editor, closeContextMenu]
  );

  React.useEffect(() => {
    if (contextMenu) {
      const handleClick = () => closeContextMenu();
      const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
          closeContextMenu();
        }
      };
      const handleScroll = () => closeContextMenu();

      document.addEventListener('click', handleClick);
      document.addEventListener('keydown', handleKeyDown);
      document.addEventListener('scroll', handleScroll, true);

      return () => {
        document.removeEventListener('click', handleClick);
        document.removeEventListener('keydown', handleKeyDown);
        document.removeEventListener('scroll', handleScroll, true);
      };
    }
  }, [contextMenu, closeContextMenu]);

  const dragHandleClassName = ['row-drag-handle', isDragging ? 'dragging' : '']
    .filter(Boolean)
    .join(' ');

  const dropIndicatorClassName = ['row-drop-indicator', dropPosition].filter(Boolean).join(' ');

  const resizeHandleClassName = ['column-resize-handle', isHoveringResize ? 'resizing' : '']
    .filter(Boolean)
    .join(' ');

  return (
    <td
      className='styled-table-cell'
      ref={ref}
      {...attributes}
      onContextMenu={handleContextMenu}
    >
      {isFirstCell && (
        <div
          className={dragHandleClassName}
          contentEditable={false}
          onMouseDown={handleDragMouseDown}
          style={{ opacity: isHovering || isDragging ? 1 : 0 }}
        >
          <span className='material-icons'>drag_indicator</span>
        </div>
      )}

      {isFirstCell && showDropIndicator && dropPosition && (
        <div className={dropIndicatorClassName} />
      )}

      {children}
      {showResizeHandle && (
        <div
          className={resizeHandleClassName}
          contentEditable={false}
          onMouseDown={handleResizeMouseDown}
          onMouseEnter={() => setIsHoveringResize(true)}
          onMouseLeave={() => setIsHoveringResize(false)}
        />
      )}
      {contextMenu &&
        ReactDOM.createPortal(
          <div
            className='table-context-menu'
            style={{ top: `${contextMenu.y}px`, left: `${contextMenu.x}px` }}
          >
            <button
              type='button'
              className='context-menu-item'
              onMouseDown={(e) => {
                e.preventDefault();
                handleAction(insertRowAbove);
              }}
            >
              <span className='material-icons'>add</span>
              Insert row above
            </button>
            <button
              type='button'
              className='context-menu-item'
              onMouseDown={(e) => {
                e.preventDefault();
                handleAction(insertRowBelow);
              }}
            >
              <span className='material-icons'>add</span>
              Insert row below
            </button>
            <div className='context-menu-divider' />
            <button
              type='button'
              className='context-menu-item'
              onMouseDown={(e) => {
                e.preventDefault();
                handleAction(insertColumnLeft);
              }}
            >
              <span className='material-icons'>add</span>
              Insert column left
            </button>
            <button
              type='button'
              className='context-menu-item'
              onMouseDown={(e) => {
                e.preventDefault();
                handleAction(insertColumnRight);
              }}
            >
              <span className='material-icons'>add</span>
              Insert column right
            </button>
            <div className='context-menu-divider' />
            <button
              type='button'
              className='context-menu-item'
              onMouseDown={(e) => {
                e.preventDefault();
                handleAction(deleteRow);
              }}
            >
              <span className='material-icons'>remove</span>
              Delete row
            </button>
            <button
              type='button'
              className='context-menu-item'
              onMouseDown={(e) => {
                e.preventDefault();
                handleAction(deleteColumn);
              }}
            >
              <span className='material-icons'>remove</span>
              Delete column
            </button>
            <div className='context-menu-divider' />
            <button
              type='button'
              className='context-menu-item danger'
              onMouseDown={(e) => {
                e.preventDefault();
                handleAction(deleteTable);
              }}
            >
              <span className='material-icons'>delete</span>
              Delete table
            </button>
          </div>,
          document.body
        )}
    </td>
  );
});

TableCellElement.displayName = 'TableCellElement';
